/** @format */

import React, { useEffect, useState } from "react";
import ReactGA from "react-ga4";
import TagManager from 'react-gtm-module';
import { Link, useHistory, useLocation } from "react-router-dom";
import CookieConsent, {
  Cookies,
  getCookieConsentValue,
} from "react-cookie-consent";

const initAnalytics = (ga4Token, gtmToken) => {
  console.debug(`initAnalytics`);

  if (ga4Token) {
    ReactGA.initialize(
      [ga4Token].map((t) => ({
        trackingId: t,
        gaOptions: {
          testMode: !t || t === "TESTTEST" || process.env.NODE_ENV === "test",
        },
        gtagOptions: {
          testMode: !t || t === "TESTTEST" || process.env.NODE_ENV === "test",
        },
      })),
    );
  }
  if (gtmToken?.substring(0, 3) === "GTM") {
    TagManager.initialize({
      gtmId: gtmToken,
    });
  }
};

const sendPageChange = (pathname, search = "") => {
  if (!pathname) return;
  const page = pathname + search;
  //ReactGA.set({ page }); // TODO - why .set() and then pageview()? Should we also .set() before .modalview()?
  //ReactGA.pageview(page);
  // TODO MO-99 do we need this or is it now automatic?
  ReactGA.send({ hitType: "page_view", page_location: page });
  console.debug(`tracker: ${page}`);
};

// TODO - just use this method signature
const sendPageView = (location) => {
  sendPageChange(location.pathname, location.search);
};
const sendModalView = (pathname) => {
  if (!pathname) return;
  //ReactGA.pageview(pathname);
  ReactGA.send({ hitType: "page_view", page_location: pathname });
  // .modalview() prepends "/modal", which I don't think we really want?
  // ReactGA.modalview(pathname);
  // console.debug(`Tracker Modal: ${pathname}`);
};

const sendEvent = (category, action, label, value, nonInteraction) => {
  let e = Object.assign(
    { category: category, action: action },
    label ? { label: label } : {},
    value ? { value: value } : {},
    nonInteraction !== undefined ? { nonInteraction: nonInteraction } : {},
  );
  console.log("Event: " + JSON.stringify(e));
  ReactGA.event(e);
};

const sendError = (description, fatal = false) => {
  ReactGA.event("exception", {
    description,
    fatal,
  });
};

export const cookieBannerMessage =
  "This website uses cookies to enhance the user experience.";

// TODO - maybe simplify this to just load on app load, and if consent changes, hard reload the whole page?

const AnalyticsTracker2 = ({
  children,
  trackingId,
  gtmTrackingId,
  onCookiesAccept,
}) => {
  const history = useHistory();
  const location = useLocation();
  const [analyticsLoaded, setAnalyticsLoaded] = useState(false);

  const loadAnalytics = () => {
    console.log("loading analytics");
    // load if we accepted after previously declined
    initAnalytics(trackingId, gtmTrackingId);
    if (onCookiesAccept) {
      onCookiesAccept();
    }
    setAnalyticsLoaded(true);
  };
  useEffect(() => {
    if (navigator.globalPrivacyControl) {
      console.log("Global Privacy Control set, not initializing analytics");
    } else if (getCookieConsentValue() === "false") {
      // getCookieConsentValue() returns a string, not boolean
      console.log("cookie consent declined");
    } else {
      // assuming no decline is acceptance, load analytics
      console.log("cookie consent allowed or implied");
      loadAnalytics();
    }
  }, [trackingId, gtmTrackingId]);

  useEffect(() => {
    if (trackingId) {
      sendPageView(history.location);
      return history.listen(sendPageView);
    }
  }, [history, trackingId]);

  if (navigator.globalPrivacyControl && !location.state?.showCookieConsent) {
    console.log("Global Privacy Control set, not rendering consent banner");
    return <>{children || null}</>;
  }

  return (
    <>
      {children || null}
      <CookieConsent
        enableDeclineButton
        visible={location.state?.showCookieConsent ? "show" : "byCookieValue"}
        declineButtonText={"Reject all"}
        buttonText={"Accept all"}
        onAccept={(byScroll) => {
          // load on click if we didn't load on page load
          if (!analyticsLoaded) {
            loadAnalytics();
          }
          history.replace(location.pathname, {}); // unset location.state
        }}
        onDecline={() => {
          // I don't think we're technically expected/required to delete existing cookies (popular WordPress plugins don't have a hook to do this, and GA/GTM doesn't have a delete-everything option, and they're they ones actually setting the cookies)
          // But we could try a few known expected cookies
          try {
            history.replace(location.pathname, {}); // unset location.state
            const allCookies = Cookies.get();
            Object.keys(allCookies).forEach((name) => {
              // TODO - try to simplify this?
              if (
                name === "_ga" ||
                name.startsWith(`_ga_${process.env.REACT_APP_GA_TOKEN}`) ||
                name.startsWith(
                  `_ga_${process.env.REACT_APP_GA_TOKEN.replace("UA-", "")}`,
                ) ||
                name.startsWith(
                  `_ga_${process.env.REACT_APP_GA4_TOKEN.replace("G-", "")}`,
                ) ||
                name.startsWith(
                  `_ga_${process.env.REACT_APP_GTM_TOKEN.replace("GTM-", "")}`,
                )
              ) {
                console.info(`Deleting GA cookie ${name}`);
                // Try to guess domains?
                Cookies.remove(name, { path: "/", domain: ".welltower.com" });
                Cookies.remove(name, { path: "/", domain: location.hostname });
              }
              // TODO - should we try to kill other cookies? Or is that potentially not our purview?
              //  else if (/^_fb.*/.test(name)) {
              //   console.info(`Deleting Facebook cookie ${name}`);
              //   Cookies.remove(name);
              // } else if (/^hubspot.*/.test(name) || /^__hs.*/.test(name)) {
              //   console.info(`Deleting Hubspot cookie ${name}`);
              //   Cookies.remove(name);
              // }
            });
          } catch (ignored) {}
          window.location.reload(); // force a full React app reload to unload analytics and Heap
        }}
        style={{
          backgroundColor: "rgba(19, 76, 106, .95)",
          color: "white",
          zIndex: 2147483647, // TalkFurther sets 2147483646, which is 2^32 - 2, so I guess 2^32 - 1 is our only option
        }}
        declineButtonStyle={{ backgroundColor: "#25628F", color: "white" }}
        buttonStyle={{ backgroundColor: "#3697b5", color: "white" }}
      >
        {cookieBannerMessage}{" "}
        <Link
          to={"/privacy-policy#cookie-notice"}
          style={{ textDecoration: "underline" }}
        >
          Cookie Notice
        </Link>
      </CookieConsent>
    </>
  );
};

export {
  initAnalytics,
  AnalyticsTracker2,
  sendPageChange,
  sendModalView,
  sendEvent,
  sendError,
};
